import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, Typography,
} from '@mui/material';
import api from '../services/api';
import { numeric } from '../quotas/QuotaTable';
import { wranglerError } from './errors';
import LogicSummary from './LogicSummary';
import { planBatches } from './quotaSets';

const PAGE = 100;
const plural = (n, word) => `${n.toLocaleString()} ${word}${n !== 1 ? 's' : ''}`;

/** Wrangler's 400 for a list of quotas as lines naming each quota: "Row 4 (North): logic: …". */
export const listErrors = (err, labelFor) => {
  const fields = err?.response?.data?.fields;
  const lines = [];
  const walk = (value, path) => {
    if (Array.isArray(value) && value.every((v) => typeof v === 'string')) lines.push(`${path}: ${value.join(' ')}`);
    else if (value && typeof value === 'object') Object.entries(value).forEach(([k, v]) => walk(v, path ? `${path}.${k}` : k));
    else if (value != null) lines.push(`${path}: ${value}`);
  };
  if (fields?.quotas && typeof fields.quotas === 'object' && !Array.isArray(fields.quotas)) {
    Object.entries(fields.quotas).forEach(([i, v]) => walk(v, labelFor(Number(i))));
  } else {
    walk(fields, '');
  }
  return lines;
};

/**
 * Check draft quotas with Wrangler's preview, show each with its summary and what it would count
 * now, then create them: in one go, or in batches when they'd write many rows (planBatches).
 * Created quotas are passed to onCreated batch by batch, so a later failure loses nothing.
 */
const CreatePreview = ({ surveyId, drafts, questions, labelFor = (i) => drafts[i]?.name || `Quota ${i + 1}`, onCreated, onBack, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [page, setPage] = useState(0);
  const [progress, setProgress] = useState(null); // {batch, created}
  const [failure, setFailure] = useState(null); // {batch, message}
  const [done, setDone] = useState(null); // {created, needsRefresh}
  const [refresh, setRefresh] = useState(null);

  useEffect(() => {
    let live = true;
    api.post(`/surveys/${surveyId}/wrangler/quotas/preview/`, { quotas: drafts })
      .then(({ data }) => { if (live) setPreview(data); })
      .catch((err) => { if (live) setPreviewError([wranglerError(err), ...listErrors(err, labelFor)].join('\n')); });
    return () => { live = false; };
    // The drafts are fixed for the life of this step.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const batches = useMemo(() => (preview ? planBatches(preview.results) : []), [preview]);
  const stats = useMemo(() => ({
    on: drafts.filter((d) => d.is_on).length,
    zero: drafts.filter((d) => d.original_limit === 0).length,
    limits: drafts.reduce((s, d) => s + (d.original_limit || 0), 0),
    needRefresh: preview ? preview.results.filter((r) => r.needs_refresh).length : 0,
    sets: [...new Set(drafts.map((d) => d.set_name).filter(Boolean))],
    inSets: drafts.filter((d) => d.set_name).length,
  }), [drafts, preview]);
  const creating = progress !== null && !failure && !done;

  const create = async (fromBatch = 0, createdSoFar = 0, refreshSoFar = false) => {
    setFailure(null);
    let created = createdSoFar;
    let needsRefresh = refreshSoFar;
    for (let b = fromBatch; b < batches.length; b += 1) {
      setProgress({ batch: b, created });
      const [start, end] = batches[b];
      let data;
      try {
        ({ data } = await api.post(`/surveys/${surveyId}/wrangler/quotas/`, { quotas: drafts.slice(start, end) }));
      } catch (err) {
        setFailure({ batch: b, created, needsRefresh, message: [wranglerError(err), ...listErrors(err, (i) => labelFor(start + i))].join('\n') });
        return;
      }
      // Only the request is guarded: once Wrangler has created a batch, it counts as created,
      // so "Try the rest again" can never send it twice.
      created += data.quotas.length;
      needsRefresh = needsRefresh || data.needs_refresh;
      onCreated(data.quotas);
    }
    setProgress({ batch: batches.length, created });
    setDone({ created, needsRefresh });
  };

  const queueRefresh = async () => {
    try {
      await api.post(`/surveys/${surveyId}/wrangler/refresh/`);
      setRefresh('queued');
    } catch (err) {
      setRefresh(wranglerError(err));
    }
  };

  if (previewError) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
          Wrangler can&apos;t create these quotas as they are. Nothing has been created.{'\n'}{previewError}
        </Alert>
        {onBack && <Button onClick={onBack} sx={{ textTransform: 'none' }}>Back</Button>}
      </Box>
    );
  }
  if (!preview) {
    return <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4 }}><CircularProgress size={22} /> Checking with Wrangler…</Box>;
  }

  const shown = drafts.slice(page * PAGE, (page + 1) * PAGE);
  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 1 }}>
        <strong>{plural(drafts.length, 'quota')}</strong>
        {stats.sets.length > 0 && (
          <>
            {stats.inSets < drafts.length && `, ${stats.inSets.toLocaleString()} of them`}
            {' '}in {stats.sets.length === 1 ? `the set "${stats.sets[0]}"` : plural(stats.sets.length, 'set')}
          </>
        )}
        {' '}· limits add up to {stats.limits.toLocaleString()} · {plural(preview.completes_considered, 'complete')} so far
      </Typography>
      {stats.on > 0 ? (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {stats.on === drafts.length ? 'All of them' : plural(stats.on, 'quota')} will be ON: they block respondents as soon as they fill.
        </Alert>
      ) : <Alert severity="info" sx={{ mb: 1 }}>They&apos;ll be OFF: they count, but block nobody until switched ON.</Alert>}
      {stats.zero > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>{plural(stats.zero, 'quota')} with a limit of 0 screen out everyone in them once ON.</Alert>
      )}
      {stats.needRefresh > 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Some completes so far don&apos;t have answers to the questions {stats.needRefresh === 1 ? 'one of these uses' : 'these use'}, so
          counts may be low until a refresh. You&apos;ll be offered one after creating.
        </Alert>
      )}
      {batches.length > 1 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          These write a lot of rows, so they&apos;ll be created in {batches.length} batches, one after another, to keep each
          short for respondents taking the survey. If one fails, the batches before it stay created.
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ my: 2, maxHeight: 420 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell sx={numeric}>Limit</TableCell>
              <TableCell sx={numeric}>Counts now</TableCell>
              <TableCell>Who it counts</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((draft, j) => {
              const i = page * PAGE + j;
              const result = preview.results[i];
              return (
                <TableRow key={i}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{draft.name}</Typography>
                    {draft.is_on && <Typography variant="caption" color="warning.dark">ON</Typography>}
                  </TableCell>
                  <TableCell sx={numeric}>{draft.original_limit}</TableCell>
                  <TableCell sx={numeric}>
                    {result.complete_count}{result.reserved_count > 0 && ` (+${result.reserved_count} holding)`}
                    {result.needs_refresh && <Typography variant="caption" color="text.secondary" display="block">may be low</Typography>}
                  </TableCell>
                  <TableCell><LogicSummary logic={draft.logic} questions={questions} /></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {drafts.length > PAGE && (
        <TablePagination component="div" count={drafts.length} page={page} onPageChange={(e, p) => setPage(p)}
          rowsPerPage={PAGE} rowsPerPageOptions={[PAGE]} />
      )}

      {progress && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate" value={(progress.created / drafts.length) * 100} sx={{ mb: 0.5 }} />
          {creating && (
            <Typography variant="body2" color="text.secondary">
              Created {progress.created.toLocaleString()} of {drafts.length.toLocaleString()}
              {batches.length > 1 && ` (batch ${progress.batch + 1} of ${batches.length})`}…
            </Typography>
          )}
        </Box>
      )}
      {failure && (
        <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
          {failure.created > 0
            ? `Created ${plural(failure.created, 'quota')}, then batch ${failure.batch + 1} of ${batches.length} failed. The rest weren't created.`
            : "Nothing was created."}{'\n'}{failure.message}
        </Alert>
      )}
      {done && (
        <Alert severity="success" sx={{ mb: 2 }}
          action={done.needsRefresh && !refresh
            ? <Button color="inherit" size="small" onClick={queueRefresh} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>Run a refresh</Button>
            : null}>
          Created {plural(done.created, 'quota')}.
          {done.needsRefresh && !refresh && ' Some counts are too low until a refresh.'}
          {refresh === 'queued' && ' Refresh queued: follow it on the Setup tab.'}
          {refresh && refresh !== 'queued' && ` The refresh couldn't be queued: ${refresh}`}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        {done ? (
          <Button variant="contained" onClick={onDone} sx={{ textTransform: 'none' }}>Close</Button>
        ) : failure ? (
          <>
            <Button onClick={onDone} sx={{ textTransform: 'none' }}>Close</Button>
            <Button variant="contained" onClick={() => create(failure.batch, failure.created, failure.needsRefresh)} sx={{ textTransform: 'none' }}>
              Try the rest again
            </Button>
          </>
        ) : (
          <>
            {onBack && <Button onClick={onBack} disabled={creating} sx={{ textTransform: 'none' }}>Back</Button>}
            <Button variant="contained" onClick={() => create()} disabled={creating} sx={{ textTransform: 'none' }}>
              {creating ? 'Creating…' : `Create ${plural(drafts.length, 'quota')}`}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default CreatePreview;
